/**
 * Gradient subsystem for the cards.
 *
 * Owns the full trump-gradient pipeline:
 *   1. `CARD_TRUMP_GRADIENTS`       — per-suit stop configs (purple → suit color).
 *   2. `addCardTrumpGradient(...)`  — resolves the master gradient. Static (non-reveal)
 *                                     trump cards share one `<linearGradient>` per suit
 *                                     in the document-level shared defs (stops are
 *                                     constant per suit); reveal-pending cards still mint
 *                                     a per-card master in their own `<defs>` — its stops
 *                                     mutate per card via `revealTrumpGradient`.
 *   3. `adjustGradient(...)`        — transform-aware clones so elements inside
 *                                     translate / scale / rotate-180 groups (pips,
 *                                     corner labels, king/ace motifs) pick up the
 *                                     correct slice of the master gradient in root
 *                                     space. Uses `href` — no stop duplication. Clones of
 *                                     the shared per-suit masters are themselves shared
 *                                     (deduplicated by master id + y1 + y2, since a clone
 *                                     is deterministic per (suit, transform context));
 *                                     clones of a per-card reveal master stay per-card.
 *   4. `revealTrumpGradient(svgEl)` — animates the trump-reveal stop-color transition
 *                                     for non-Jack trump cards.
 *
 * The cards' trump gradients live in `userSpaceOnUse` coordinates.
 */

import { SVG_NS } from "./card-svg.js";
import { ANIMATION, TRUMP_COLOR, SUIT_COLORS } from "../constants.js";
import { getSharedDefs } from "./shared-defs.js";

let gradCloneCounter = 0;
let cardGradCounter = 0;
const sharedTrumpGradientIds = new Map(); // suit -> "ctg-<suit>"
const sharedGradCloneIds = new Map(); // "masterId|y1|y2" -> cloneId

export const CARD_TRUMP_GRADIENTS = {
  diamonds: {
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "38%", color: TRUMP_COLOR },
      { offset: "85%", color: SUIT_COLORS.diamonds },
    ],
  },
  hearts: {
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "35%", color: TRUMP_COLOR },
      { offset: "80%", color: SUIT_COLORS.hearts },
    ],
  },
  spades: {
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "38%", color: TRUMP_COLOR },
      // Not one of the shared five hex registrations — a genuine blended intermediate
      // tone specific to this gradient (also hand-typed in cards.js's spades stop).
      { offset: "58%", color: "#2D5A4A" },
      { offset: "85%", color: SUIT_COLORS.spades },
    ],
  },
  clubs: {
    stops: [
      { offset: "0%", color: TRUMP_COLOR },
      { offset: "38%", color: TRUMP_COLOR },
      { offset: "85%", color: SUIT_COLORS.clubs },
    ],
  },
};

/** Card-height linearGradient shell (id + userSpaceOnUse geometry, no stops yet). */
function buildGradientShell(id) {
  const lg = document.createElementNS(SVG_NS, "linearGradient");
  lg.id = id;
  lg.setAttribute("gradientUnits", "userSpaceOnUse");
  lg.setAttribute("x1", "0");
  lg.setAttribute("y1", "0");
  lg.setAttribute("x2", "0");
  lg.setAttribute("y2", "350");
  return lg;
}

/**
 * Resolves the master trump gradient for a card. Static trump cards share one
 * `<linearGradient>` per suit (stop geometry is constant per suit) minted once in the
 * document-level shared defs; reveal-pending cards get a fresh per-card master whose
 * stops start at `suitColor` and animate later via `revealTrumpGradient`. Returns the
 * gradient URL string (e.g., 'url(#ctg-diamonds)' or 'url(#ctg-0)').
 */
export function addCardTrumpGradient(svg, suit, suitColor, forReveal) {
  if (!forReveal) return getSharedTrumpGradient(suit);
  return addRevealGradient(svg, suit, suitColor);
}

/** Creates (once per suit) the shared static master gradient in the document-level defs. */
function getSharedTrumpGradient(suit) {
  const cached = sharedTrumpGradientIds.get(suit);
  if (cached != null) return `url(#${cached})`;

  const id = `ctg-${suit}`;
  const lg = buildGradientShell(id);
  for (const { offset, color } of CARD_TRUMP_GRADIENTS[suit].stops) {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", color);
    lg.appendChild(stop);
  }
  getSharedDefs().appendChild(lg);
  sharedTrumpGradientIds.set(suit, id);
  return `url(#${id})`;
}

/** Creates a per-card reveal-pending master: stops start at `suitColor`, tween later. */
function addRevealGradient(svg, suit, suitColor) {
  const id = `ctg-${cardGradCounter++}`;
  const lg = buildGradientShell(id);
  for (const { offset, color } of CARD_TRUMP_GRADIENTS[suit].stops) {
    const stop = document.createElementNS(SVG_NS, "stop");
    stop.setAttribute("offset", offset);
    stop.setAttribute("stop-color", suitColor);
    stop.classList.add("trump-grad-stop");
    stop.dataset.targetColor = color;
    lg.appendChild(stop);
  }
  const defs = document.createElementNS(SVG_NS, "defs");
  defs.appendChild(lg);
  svg.appendChild(defs);
  return `url(#${id})`;
}

/** True for a shared per-suit static master id ("ctg-diamonds"), false for a per-card reveal id ("ctg-0"). */
function isSharedMasterId(masterId) {
  return (
    masterId.startsWith("ctg-") &&
    CARD_TRUMP_GRADIENTS[masterId.slice(4)] != null
  );
}

function appendClone(defs, cloneId, masterId, y1, y2) {
  const clone = document.createElementNS(SVG_NS, "linearGradient");
  clone.id = cloneId;
  clone.setAttribute("href", `#${masterId}`);
  clone.setAttribute("gradientUnits", "userSpaceOnUse");
  clone.setAttribute("x1", "0");
  clone.setAttribute("y1", String(y1));
  clone.setAttribute("x2", "0");
  clone.setAttribute("y2", String(y2));
  defs.appendChild(clone);
}

/**
 * Creates a lightweight gradient clone with y1/y2 adjusted for a transform context.
 * Inherits stops from the master gradient via href — no duplication.
 * For translate(tx,ty) scale(s): y1=-ty/s, y2=(350-ty)/s.
 * For flipped (inside rotate(180)): y1/y2 are swapped.
 * Returns color unchanged if it's not a gradient URL.
 *
 * A clone of a shared per-suit master is itself shared: it's deterministic per (suit,
 * transform context), so a clone of a shared master lives in the shared defs too,
 * deduplicated by master id + y1 + y2 (one clone per distinct key, not one per pip). A
 * clone of a per-card reveal master stays per-card — its master's stops mutate, so
 * nothing about it can be shared across cards.
 */
export function adjustGradient(parent, color, ty, scale, isFlipped) {
  if (typeof color !== "string" || !color.startsWith("url(#")) return color;
  if (scale === 1 && ty === 0 && !isFlipped) return color;

  const masterId = color.slice(5, -1);
  const y1 =
    Math.round((isFlipped ? (350 - ty) / scale : -ty / scale) * 100) / 100;
  const y2 =
    Math.round((isFlipped ? -ty / scale : (350 - ty) / scale) * 100) / 100;

  if (isSharedMasterId(masterId)) {
    const key = `${masterId}|${y1}|${y2}`;
    const cached = sharedGradCloneIds.get(key);
    if (cached != null) return `url(#${cached})`;
    const cloneId = `${masterId}-c${sharedGradCloneIds.size}`;
    appendClone(getSharedDefs(), cloneId, masterId, y1, y2);
    sharedGradCloneIds.set(key, cloneId);
    return `url(#${cloneId})`;
  }

  const svg = parent.ownerSVGElement || parent;
  const defs = svg.querySelector("defs");
  if (!defs) return color;
  const cloneId = `${masterId}-c${gradCloneCounter++}`;
  appendClone(defs, cloneId, masterId, y1, y2);
  return `url(#${cloneId})`;
}

/**
 * Animates a non-Jack trump card's gradient stops from suit color to their target
 * trump colors (the trump-reveal transition). Jacks use a separate solid-fill path
 * handled in `revealTrumpColor`.
 */
export function revealTrumpGradient(svgEl) {
  svgEl.querySelectorAll(".trump-grad-stop").forEach((stop) => {
    stop.style.transition = `stop-color ${ANIMATION.TRUMP_REVEAL}ms ease-out`;
    stop.style.stopColor = stop.dataset.targetColor;
  });
}
